import Email from "@/domain/entities/emails/Email";
import AIResponse, {
    AssistantAnswer,
    AssistantDraft,
    ComponentDataContentType,
    ContentAction,
    ContentDataTypes,
    MoreDataCallbackResult,
    MoreDataContent,
} from "@/domain/entities/vistassistant/AIResponse";
import type {
    CreateUserVoiceRequestData,
    ReportBugData,
    SearchSimilarTicketsResult,
} from "@/domain/entities/vistassistant/TicketTypes";
import agendaItemMapper from "@/mappers/agenda/agendaItemMapper";
import createAgendaItemDataMapper from "@/mappers/agenda/createAgendaItemDataMapper";
import { emailMapper } from "@/mappers/email/emailMapper";
import {
    AgendaItem as AgendaItemSDK,
    AgendaTimeSlot as AgendaTimeSlotSDK,
    AssistantAnswer as AssistantAnswerSDK,
    AssistantDraft as AssistantDraftSDK,
    ComponentData,
    CreateAgendaItemData as CreateAgendaItemDataSDK,
    Mail,
} from "@cs_teamwaydev/mailvista-sdk_react";
import agendaTimeSlotMapper from "../agenda/agendaTimeSlotMapper";

type SDKMoreDataItem = AgendaTimeSlotSDK | Mail | AgendaItemSDK;

const isMailSDK = (item: SDKMoreDataItem): item is Mail =>
    "internetMessageId" in item && "conversationId" in item;

const isAgendaTimeSlotSDK = (
    item: SDKMoreDataItem,
): item is AgendaTimeSlotSDK =>
    "startDate" in item && "endDate" in item && !("internetMessageId" in item);

const isAgendaItemSDK = (item: SDKMoreDataItem): item is AgendaItemSDK =>
    "isAllDay" in item && !("internetMessageId" in item);

type SDKMoreDataResult = {
    userRequestId: string;
    content: SDKMoreDataItem[];
    moreDataCallBack?: () => Promise<SDKMoreDataResult>;
};

const mapMoreDataContent = (
    content: SDKMoreDataItem[],
    type: ComponentDataContentType,
): MoreDataContent => {
    if (type === ComponentDataContentType.FreeTimeSlots) {
        return content
            .filter(isAgendaTimeSlotSDK)
            .map((slot) => agendaTimeSlotMapper(slot));
    }
    if (type === ComponentDataContentType.AgendaItems) {
        return content
            .filter(isAgendaItemSDK)
            .map((event) => agendaItemMapper(event));
    }

    return content.filter(isMailSDK).map((mail) => emailMapper(mail));
};

const mapMoreDataResult = async (
    result: SDKMoreDataResult,
    type: ComponentDataContentType,
): Promise<MoreDataCallbackResult> => ({
    userRequestId: result.userRequestId,
    content: mapMoreDataContent(result.content, type),
    moreDataCallBack: result.moreDataCallBack
        ? async () => mapMoreDataResult(await result.moreDataCallBack!(), type)
        : undefined,
});

/** Maps each value of a by-ref record; an element that cannot be read is left out (its ref stays text). */
const mapRefs = <S, T>(
    record: Record<string, S>,
    mapper: (item: S) => T,
): Record<string, T> =>
    Object.fromEntries(
        Object.entries(record).flatMap(([ref, item]) => {
            try {
                return [[ref, mapper(item)]];
            } catch (error) {
                console.error(error, item);

                return [];
            }
        }),
    );

/** The draft, with the mail it answers; that mail unreadable, the draft is left out (the answer stays). */
const draftMapper = (draft: AssistantDraftSDK): AssistantDraft | undefined => {
    if (!draft.replyTo) {
        return {
            body: draft.body,
            ...(draft.subject ? { subject: draft.subject } : {}),
        };
    }
    try {
        return { body: draft.body, replyTo: emailMapper(draft.replyTo) };
    } catch (error) {
        console.error(error, draft.replyTo);

        return undefined;
    }
};

const answerMapper = (answer: AssistantAnswerSDK): AssistantAnswer => {
    const draft = answer.draft ? draftMapper(answer.draft) : undefined;

    return {
        text: answer.text,
        emails: mapRefs(answer.mails, emailMapper),
        ...(answer.sentMails?.length ? { sentRefs: answer.sentMails } : {}),
        ...(answer.question ? { question: answer.question } : {}),
        ...(answer.slotReplies ? { slotReplies: answer.slotReplies } : {}),
        events: mapRefs(answer.events, agendaItemMapper),
        slots: mapRefs(answer.slots, agendaTimeSlotMapper),
        ...(draft ? { draft } : {}),
        ...(answer.unknownNames?.length
            ? { unknownNames: answer.unknownNames }
            : {}),
    };
};

const AIResponseMapper = (response: ComponentData): AIResponse => {
    const type = response.content.contentType as ComponentDataContentType;

    let contentData: ContentDataTypes = "";
    if (type === ComponentDataContentType.Mails) {
        if (!Array.isArray(response.content.contentData)) {
            throw new Error("Mails content data must be an array");
        }
        contentData = response.content.contentData
            .map((item) => {
                try {
                    return emailMapper(item as Mail);
                } catch (error) {
                    console.error(error, item);

                    return undefined;
                }
            })
            .filter((item) => item !== undefined) as Email[];
    } else if (type === ComponentDataContentType.AgendaItems) {
        if (!Array.isArray(response.content.contentData)) {
            throw new Error("AgendaItems content data must be an array");
        }
        contentData = response.content.contentData.map((item) =>
            agendaItemMapper(item as AgendaItemSDK),
        );
    } else if (type === ComponentDataContentType.CreateAgendaItem) {
        contentData = createAgendaItemDataMapper(
            response.content.contentData as CreateAgendaItemDataSDK,
        );
    } else if (type === ComponentDataContentType.FreeTimeSlots) {
        if (!Array.isArray(response.content.contentData)) {
            throw new Error("FreeTimeSlots content data must be an array");
        }
        contentData = response.content.contentData.map((item) =>
            agendaTimeSlotMapper(item as AgendaTimeSlotSDK),
        );
    } else if (
        type === ComponentDataContentType.ReportBug ||
        type === ComponentDataContentType.CreateUserVoiceRequest ||
        type === ComponentDataContentType.SearchSimilarTickets ||
        type === ComponentDataContentType.CollectBugInfo ||
        type === ComponentDataContentType.CollectVoiceInfo ||
        type === ComponentDataContentType.SupportAnswer
    ) {
        contentData = response.content.contentData as
            | ReportBugData
            | CreateUserVoiceRequestData
            | SearchSimilarTicketsResult
            | string;
    } else if (typeof response.content.contentData === "string") {
        contentData = response.content.contentData;
    }

    return new AIResponse({
        userRequestId: response.userRequestId,
        aiComment: response.aiComment,
        content: {
            contentData,
            contentType: response.content
                .contentType as ComponentDataContentType,
            contentAction: response.content.contentAction as ContentAction,
            upcomingAnalysis: response.content.upcomingAnalysis,
            moreDataCallBack: response.content.moreDataCallBack
                ? async () => {
                      const result = (await response.content
                          .moreDataCallBack!()) as SDKMoreDataResult;
                      return mapMoreDataResult(result, type);
                  }
                : undefined,
        },
        aiVocalText: response.aiVocalText,
        deepAnalysis: {
            result: undefined,
            error: undefined,
        },
        errorCause: response.errorCause,
        // Steps and thoughts both stay: the block shows them folded under the answer.
        reasoning: response.reasoning,
        answer: response.answer ? answerMapper(response.answer) : undefined,
        answerModel: response.answerModel,
        turnId: response.turnId,
    });
};

export default AIResponseMapper;
